import React, { useMemo } from "react";

import FunctionalButton from "components/layout/FunctionalButton";
import Portal from "components/layout/Portal";

export type MapProgressSnapshot = {
    phase: string;
    progress: number;
    message: string;
};

export type MapProgressFlow = "load" | "save" | "clear" | "generic";

const LOAD_STEPS = [
    { id: "fetch", label: "Fetch" },
    { id: "upload", label: "Upload" },
    { id: "relocalization", label: "Relocalize" },
    { id: "reinit", label: "Reinit" },
    { id: "wait_metadata", label: "Live map" },
    { id: "apply_filter", label: "Filter" },
] as const;

const SAVE_STEPS = [
    { id: "download", label: "Download" },
    { id: "flush", label: "Write" },
    { id: "export", label: "Export" },
] as const;

function phaseLabel(phase: string, flow: MapProgressFlow): string {
    switch (phase) {
        case "download":
            return "Downloading map from device";
        case "upload":
        case "upload_map":
            return "Uploading map to device";
        case "fetch":
            return "Fetching map from storage";
        case "flush":
            return "Writing map file";
        case "export":
            return "Exporting 2D preview";
        case "relocalization":
            return "Relocalizing on Aurora";
        case "clear":
            return flow === "clear" ? "Clearing map…" : "Clearing map on device";
        case "reinit":
            return flow === "clear" ? "Clearing map…" : "Reinitializing Aurora";
        case "wait_metadata":
            return "Waiting for live map";
        case "apply_filter":
            return "Applying keep-out filter";
        case "create_goal":
            return "Starting map save";
        case "map_sync":
            return flow === "clear" ? "Clearing map…" : "Syncing explore map";
        case "idle":
            return "Finishing";
        case "running":
            return "Working";
        default:
            return phase.replace(/_/g, " ") || "Working";
    }
}

function inferFlow(title: string, phase: string): MapProgressFlow {
    const t = title.toLowerCase();
    if (t.includes("clear")) return "clear";
    if (t.includes("load")) return "load";
    if (t.includes("sav")) return "save";
    if (phase === "clear") return "clear";
    if (
        [
            "fetch",
            "upload",
            "upload_map",
            "relocalization",
            "reinit",
            "map_sync",
            "wait_metadata",
            "apply_filter",
        ].includes(phase)
    ) {
        return "load";
    }
    if (["download", "flush", "export", "create_goal"].includes(phase)) {
        return "save";
    }
    return "generic";
}

function normalizeStepPhase(phase: string, flow: MapProgressFlow): string {
    if (phase === "upload_map") return "upload";
    if (phase === "create_goal") return "download";
    // Explore map sync during LoadMap is part of the reinit step.
    if (phase === "map_sync" && flow !== "clear") return "reinit";
    return phase;
}

/** Backend clear_map reports reinit/map_sync; keep a single user-facing stage. */
function displayPhaseForFlow(phase: string, flow: MapProgressFlow): string {
    if (flow !== "clear") return phase;
    if (
        phase === "clear" ||
        phase === "reinit" ||
        phase === "map_sync" ||
        phase === "idle"
    ) {
        return "clear";
    }
    return phase;
}

function isClearFlowBackendDetail(message: string): boolean {
    const lower = message.toLowerCase();
    return (
        lower.includes("reinit") ||
        lower.includes("reinitializ") ||
        lower.includes("explore map") ||
        lower.includes("sdk")
    );
}

/** Prefer "(N%)" embedded in status_message when present so the bar matches the detail line. */
function percentFromStatusMessage(message: string): number | null {
    const m = message.match(/\((\d{1,3})\s*%\)/);
    if (!m) return null;
    const n = Number(m[1]);
    if (!Number.isFinite(n)) return null;
    return Math.min(100, Math.max(0, Math.round(n)));
}

/**
 * Clear pipeline often reports phase changes at progress=0. Synthesize a bar
 * position from phase so the overlay is not stuck at 0% the whole time.
 */
function syntheticClearProgress(phase: string): number | null {
    switch (phase) {
        case "clear":
            return 0.25;
        case "reinit":
            return 0.55;
        case "map_sync":
            return 0.85;
        case "idle":
            return 1;
        default:
            return null;
    }
}

function stepState(
    stepId: string,
    currentPhase: string,
    steps: readonly { id: string; label: string }[],
    flow: MapProgressFlow,
): "done" | "current" | "pending" {
    const cur = normalizeStepPhase(currentPhase, flow);
    const curIdx = steps.findIndex((s) => s.id === cur);
    const stepIdx = steps.findIndex((s) => s.id === stepId);
    if (stepIdx < 0) return "pending";
    if (curIdx < 0) return "pending";
    if (stepIdx < curIdx) return "done";
    if (stepIdx === curIdx) return "current";
    return "pending";
}

const cancelButtonClassName =
    "rounded-r5 border border-red-600 bg-red-600 px-3 py-1 text-h5 text-white hover:border-red-500 hover:bg-red-500";

const MapStorageProgressOverlay: React.FC<{
    visible: boolean;
    title: string;
    snapshot: MapProgressSnapshot | null;
    onCancel?: () => void;
    cancelling?: boolean;
}> = ({ visible, title, snapshot, onCancel, cancelling = false }) => {
    const phase = snapshot?.phase ?? "idle";
    const progress = snapshot?.progress ?? 0;
    const rawMessage = snapshot?.message?.trim() ?? "";
    const flow = useMemo(() => inferFlow(title, phase), [title, phase]);
    const displayPhase = displayPhaseForFlow(phase, flow);
    const statusMessage =
        flow === "clear" && isClearFlowBackendDetail(rawMessage) ? "" : rawMessage;
    const fromMessage = percentFromStatusMessage(statusMessage);
    const clearSynthetic =
        flow === "clear" && !(typeof progress === "number" && progress > 0)
            ? syntheticClearProgress(phase)
            : null;
    const effectiveProgress =
        clearSynthetic != null
            ? clearSynthetic
            : typeof progress === "number"
              ? progress
              : 0;
    const determinate = typeof effectiveProgress === "number" && effectiveProgress >= 0;
    const pct =
        fromMessage != null
            ? fromMessage
            : determinate
              ? Math.round(Math.min(100, Math.max(0, effectiveProgress * 100)))
              : null;
    const primary = phaseLabel(displayPhase, flow);
    const steps =
        flow === "load"
            ? LOAD_STEPS
            : flow === "save"
              ? SAVE_STEPS
              : null;

    return (
        <Portal visible={visible}>
            <div
                className="fixed inset-0 z-[10002] flex items-center justify-center bg-black bg-opacity-60"
                role="dialog"
                aria-modal="true"
                aria-labelledby="map-storage-progress-title"
            >
                <div className="mx-4 w-full max-w-md rounded-r10 border border-black80 bg-black90 p-4 shadow-lg">
                    <h3 id="map-storage-progress-title" className="text-h4 mb-3">
                        {title}
                    </h3>
                    <p className="text-h5 mb-1 font-semibold text-black40">{primary}</p>
                    {statusMessage && statusMessage.toLowerCase() !== primary.toLowerCase() ? (
                        <p className="text-h5 text-black60 mb-3 whitespace-pre-wrap">
                            {statusMessage}
                        </p>
                    ) : (
                        <p className="text-h5 text-black60 mb-3">
                            {pct != null ? `${pct}%` : "In progress…"}
                        </p>
                    )}
                    {steps ? (
                        <ol className="mb-3 flex flex-wrap gap-1.5">
                            {steps.map((step) => {
                                const state = stepState(
                                    step.id,
                                    displayPhase,
                                    steps,
                                    flow,
                                );
                                const chipClass =
                                    state === "current"
                                        ? "border-primary2 bg-primary2/20 text-white"
                                        : state === "done"
                                          ? "border-black70 bg-black80 text-black40"
                                          : "border-black80 bg-black70/40 text-black60";
                                return (
                                    <li
                                        key={step.id}
                                        className={`text-h5 rounded-r5 border px-2 py-0.5 ${chipClass}`}
                                    >
                                        {state === "done" ? "✓ " : ""}
                                        {step.label}
                                    </li>
                                );
                            })}
                        </ol>
                    ) : null}
                    <div className="h-2 w-full overflow-hidden rounded-r5 bg-black70">
                        <div
                            className="h-full bg-primary2 transition-[width] duration-150 ease-out"
                            style={{ width: `${pct ?? 0}%` }}
                        />
                    </div>
                    <p className="mt-2 text-right text-h5 text-black60">
                        {pct != null ? `${pct}%` : "…"}
                    </p>
                    {onCancel ? (
                        <div className="mt-4 flex justify-end">
                            <FunctionalButton
                                title={cancelling ? "Cancelling…" : "Cancel"}
                                className={cancelButtonClassName}
                                disabled={cancelling}
                                onClick={onCancel}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </Portal>
    );
};

export default MapStorageProgressOverlay;
